import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { and, eq } from "drizzle-orm";
import { db, recipeClones, recipeLikes } from "@/db/index.server";
import { auth } from "@/lib/auth";
import { RecipeDetails } from "@/components/views/recipes/recipe-details";
import { ContainerSmall } from "@/components/primitives/container-small";
import { PageContainer } from "@/components/primitives/layout";
import { formatGrams, formatKcal } from "@/lib/formatters";
import { SCOOP_SIZE_GRAMS } from "@/lib/constants";
import { RECIPE_TYPE_MAP } from "@/lib/recipeType";
import { transformRecipe } from "@/lib/recipes/transformRecipe";
import { getRecipeBySlug } from "@/lib/recipes/getRecipeBySlug";
import { getRecipeCalculations } from "@/lib/recipes/getRecipeCalculations";
import { getRecipeIngredients } from "@/lib/recipes/getRecipeIngredients";
import type { WithContext, Recipe as SchemaRecipe } from "schema-dts";
import { getRecipeImageUrl } from "@/lib/recipes/recipeVisual";

const getRecipeData = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const request = getRequest();
    const session = await auth.api.getSession({ headers: request.headers });
    const userId = session?.user?.id ?? null;

    const dbRecipe = await getRecipeBySlug(data.slug, userId);
    if (!dbRecipe) return null;

    let likedByUser = false;
    if (userId) {
      const like = await db._query.recipeLikes.findFirst({
        where: and(
          eq(recipeLikes.recipeId, dbRecipe.id),
          eq(recipeLikes.userId, userId),
        ),
        columns: { id: true },
      });
      likedByUser = Boolean(like);
    }

    const clonedFromRecipe =
      (
        await db._query.recipeClones.findFirst({
          where: eq(recipeClones.clonedRecipeId, dbRecipe.id),
          with: {
            sourceRecipe: {
              columns: {
                id: true,
                slug: true,
                name: true,
              },
            },
          },
        })
      )?.sourceRecipe ?? null;

    const recipe = {
      ...transformRecipe({ ...dbRecipe, likedByUser }),
      clonedFromRecipe,
    };
    const calculations = getRecipeCalculations(recipe);
    const canEdit = recipe.createdBy === userId;

    return { recipe, calculations, canEdit };
  });

export const Route = createFileRoute("/recipes/$slug/")({
  loader: async ({ params }) => {
    const data = await getRecipeData({ data: { slug: params.slug } });
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    return {
      meta: [
        { title: `${loaderData.recipe.name} - Scoopulator` },
        {
          name: "description",
          content:
            loaderData.recipe.description ||
            "View this ice cream recipe on Scoopulator",
        },
        {
          property: "og:title",
          content: `${loaderData.recipe.name} - Scoopulator`,
        },
        {
          property: "og:description",
          content:
            loaderData.recipe.description ||
            "View this ice cream recipe on Scoopulator",
        },
        {
          property: "og:image",
          content: getRecipeImageUrl(loaderData.recipe.image) || "/logo.png",
        },
      ],
    };
  },
  component: RecipeDetailsPage,
});

function RecipeDetailsPage() {
  const { recipe, calculations, canEdit } = Route.useLoaderData();
  const ingredients = getRecipeIngredients(recipe);
  const perServingMultipler = SCOOP_SIZE_GRAMS / calculations.totalWeight;

  const structuredData: WithContext<SchemaRecipe> = {
    "@context": "https://schema.org",
    "@type": "Recipe",
    name: recipe.name,
    description: recipe.description,
    url: `https://scoopulator.app/recipes/${recipe.slug}`,
    image:
      getRecipeImageUrl(recipe.image) ||
      "https://scoopulator.app/logo-scoop.svg",
    author: recipe.createdByName
      ? { "@type": "Person", name: recipe.createdByName }
      : undefined,
    datePublished: new Date(recipe.createdAt).toISOString(),
    dateModified: new Date(recipe.updatedAt).toISOString(),
    isAccessibleForFree: true,
    recipeCategory: RECIPE_TYPE_MAP[recipe.type]?.label ?? "Ice cream",
    recipeIngredient: ingredients.map(
      (ri) => `${ri.amountGrams}g ${ri.ingredient.name}`,
    ),
    nutrition: {
      "@type": "NutritionInformation",
      calories: formatKcal(calculations.totalEnergyKcal * perServingMultipler),
      fatContent: formatGrams(calculations.totalFatGrams * perServingMultipler),
      carbohydrateContent: formatGrams(
        calculations.totalCarbohydrateGrams * perServingMultipler,
      ),
      proteinContent: formatGrams(
        calculations.totalProteinGrams * perServingMultipler,
      ),
      sugarContent: formatGrams(
        calculations.totalSugarGrams * perServingMultipler,
      ),
      fiberContent: formatGrams(
        calculations.totalFiberGrams * perServingMultipler,
      ),
      servingSize: formatGrams(SCOOP_SIZE_GRAMS),
    },
    recipeYield: formatGrams(calculations.totalWeight),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData).replace(/</g, "\\u003c"),
        }}
      />
      <PageContainer>
        <ContainerSmall>
          <RecipeDetails
            key={recipe.id}
            recipe={recipe}
            canEdit={canEdit}
            calculations={calculations}
          />
        </ContainerSmall>
      </PageContainer>
    </>
  );
}
